import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getClient, updateClient, getPropertyAddresses, createPropertyAddress, updatePropertyAddress, deletePropertyAddress, getQuotes, getJobs, getInvoices, getStaff, createComment, updateComment, deleteComment, getPropertyManagers, createPropertyManager, updatePropertyManager, getReferrals, createQuote } from '../services/api';
import { formatDate, formatDateTime, getPerthDateOffset } from '../utils/dateUtils';
import { ArrowLeft, Building2, User, Phone, Mail, MapPin, Loader2, Edit, Plus, X, Trash2, Save, Home, Briefcase, FileText, AlertTriangle, ChevronRight, MessageCircle, AlertCircle, Send, ExternalLink, UserPlus } from 'lucide-react';
import { RichTextEditor } from '../components/RichTextEditor';
import { DatePicker } from '../components/DateTimePicker';
import { PropertyAddressModal } from '../components/clients/PropertyAddressModal';
import { SearchableSelect } from '../components/SearchableSelect';
import { useAuth } from '../contexts/AuthContext';
import { canSeePrices } from '../utils/permissions';

const clientTypes = { 1: 'Owner', 2: 'Real State/Strata', 3: 'Tenant' };

const mapsLink = (address) => address ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}` : null;
const AddressLink = ({ address, className = "text-[#3498db] hover:underline" }) => {
  if (!address) return <span>-</span>;
  return <a href={mapsLink(address)} target="_blank" rel="noopener noreferrer" className={`${className} inline-flex items-center gap-1`} data-testid="address-map-link">{address}<ExternalLink size={12} className="opacity-60" /></a>;
};

// Edit Client Modal - Full form matching original system
function EditClientModal({ isOpen, onClose, onSubmit, loading, client }) {
  const [formData, setFormData] = useState({});
  const [sameAsPostal, setSameAsPostal] = useState(true);

  const { data: referrals } = useQuery({
    queryKey: ['referrals'],
    queryFn: () => getReferrals().then(r => r.data),
    enabled: isOpen
  });

  useEffect(() => {
    if (client) {
      setFormData({
        ...client,
        // Map API field names (po_/sa_) to form field names (postal_/street_)
        postal_address_line1: client.po_address_line1 || client.postal_address_line1 || '',
        postal_address_line2: client.po_address_line2 || client.postal_address_line2 || '',
        postal_address_line3: client.po_address_line3 || client.postal_address_line3 || '',
        postal_address_line4: client.po_address_line4 || client.postal_address_line4 || '',
        postal_city: client.po_city || client.postal_city || '',
        postal_region: client.po_region || client.postal_region || '',
        postal_postal_code: client.po_postal_code || client.postal_postal_code || '',
        postal_country: client.po_country || client.postal_country || '',
        street_address_line1: client.sa_address_line1 || client.street_address_line1 || '',
        street_address_line2: client.sa_address_line2 || client.street_address_line2 || '',
        street_city: client.sa_city || client.street_city || '',
        street_region: client.sa_region || client.street_region || '',
        street_postal_code: client.sa_postal_code || client.street_postal_code || '',
        street_country: client.sa_country || client.street_country || '',
      });
      setSameAsPostal(
        (!client.sa_address_line1 && !client.street_address_line1) ||
        (client.sa_address_line1 || '') === (client.po_address_line1 || '') ||
        (client.street_address_line1 || '') === (client.postal_address_line1 || '')
      );
    }
  }, [client]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.contact_name) { toast.error('Please enter client name'); return; }
    const data = { ...formData };
    if (sameAsPostal) {
      data.street_address_line1 = data.postal_address_line1;
      data.street_address_line2 = data.postal_address_line2;
      data.street_address_line3 = data.postal_address_line3;
      data.street_address_line4 = data.postal_address_line4;
      data.street_city = data.postal_city;
      data.street_region = data.postal_region;
      data.street_postal_code = data.postal_postal_code;
      data.street_country = data.postal_country;
    }
    // Map the modal's form field names back to the canonical po_/sa_ fields the
    // rest of the app reads (header, getFullAddress, PDFs, modal re-init). Without
    // this, edited addresses were saved to dead postal_/street_ fields and the
    // client kept showing the old address after "Save".
    data.po_address_line1 = data.postal_address_line1;
    data.po_address_line2 = data.postal_address_line2;
    data.po_address_line3 = data.postal_address_line3;
    data.po_address_line4 = data.postal_address_line4;
    data.po_city = data.postal_city;
    data.po_region = data.postal_region;
    data.po_postal_code = data.postal_postal_code;
    data.po_country = data.postal_country;
    data.sa_address_line1 = data.street_address_line1;
    data.sa_address_line2 = data.street_address_line2;
    data.sa_address_line3 = data.street_address_line3;
    data.sa_address_line4 = data.street_address_line4;
    data.sa_city = data.street_city;
    data.sa_region = data.street_region;
    data.sa_postal_code = data.street_postal_code;
    data.sa_country = data.street_country;
    onSubmit(data);
  };

  if (!isOpen) return null;

  const clientTypes = { 1: 'Owner', 2: 'Real State/Strata', 3: 'Tenant' };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b flex items-center justify-between bg-slate-50">
          <h2 className="text-lg font-semibold text-slate-700 flex items-center gap-2"><User size={20} />Edit Client Information</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={24} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Client Info */}
          <div className="space-y-4">
            <div className="grid grid-cols-[120px_1fr] items-center gap-4">
              <label className="text-sm text-slate-600 text-right">Name</label>
              <input type="text" value={formData.contact_name || ''} onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })} className="px-3 py-2 border rounded text-sm" data-testid="edit-client-name" />
            </div>
            <div className="grid grid-cols-[120px_1fr] items-center gap-4">
              <label className="text-sm text-slate-600 text-right">Website</label>
              <input type="text" value={formData.website || ''} onChange={(e) => setFormData({ ...formData, website: e.target.value })} className="px-3 py-2 border rounded text-sm" data-testid="edit-client-website" />
            </div>
            <div className="grid grid-cols-[120px_1fr] items-center gap-4">
              <label className="text-sm text-slate-600 text-right">Tax Number</label>
              <input type="text" value={formData.tax_number || ''} onChange={(e) => setFormData({ ...formData, tax_number: e.target.value })} className="px-3 py-2 border rounded text-sm" data-testid="edit-client-tax" />
            </div>
            <div className="grid grid-cols-[120px_1fr] items-center gap-4">
              <label className="text-sm text-slate-600 text-right">Type</label>
              <select value={formData.client_type || 1} onChange={(e) => setFormData({ ...formData, client_type: parseInt(e.target.value) })} className="px-3 py-2 border rounded text-sm" data-testid="edit-client-type">
                {Object.entries(clientTypes).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-[120px_1fr] items-center gap-4">
              <label className="text-sm text-slate-600 text-right">Referral</label>
              <select value={formData.referral_id || ''} onChange={(e) => setFormData({ ...formData, referral_id: e.target.value })} className="px-3 py-2 border rounded text-sm" data-testid="edit-client-referral">
                <option value="">Select Referral</option>
                {referrals?.map(r => <option key={r.id} value={r.id}>{r.name || r.contact_name}</option>)}
              </select>
            </div>
          </div>

          {/* Contact Person */}
          <div>
            <h3 className="text-sm font-semibold text-[#f39c12] mb-4 flex items-center gap-2 border-b pb-2"><User size={16} />Contact Person</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-[120px_1fr] items-center gap-4">
                <label className="text-sm text-slate-600 text-right">Name</label>
                <div className="grid grid-cols-2 gap-3">
                  <input type="text" placeholder="First name" value={formData.contact_first_name || ''} onChange={(e) => setFormData({ ...formData, contact_first_name: e.target.value })} className="px-3 py-2 border rounded text-sm" data-testid="edit-client-first-name" />
                  <input type="text" placeholder="Last name" value={formData.contact_last_name || ''} onChange={(e) => setFormData({ ...formData, contact_last_name: e.target.value })} className="px-3 py-2 border rounded text-sm" data-testid="edit-client-last-name" />
                </div>
              </div>
              <div className="grid grid-cols-[120px_1fr] items-center gap-4">
                <label className="text-sm text-slate-600 text-right">Email Address</label>
                <input type="email" value={formData.contact_email || ''} onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })} className="px-3 py-2 border rounded text-sm" data-testid="edit-client-email" />
              </div>
              <div className="grid grid-cols-[120px_1fr] items-center gap-4">
                <label className="text-sm text-slate-600 text-right">Phone Number</label>
                <input type="text" value={formData.contact_phone || ''} onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })} className="px-3 py-2 border rounded text-sm" data-testid="edit-client-phone" />
              </div>
            </div>
          </div>

          {/* Postal Address */}
          <div>
            <h3 className="text-sm font-semibold text-[#f39c12] mb-4 flex items-center gap-2 border-b pb-2"><MapPin size={16} />Postal Address</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-[120px_1fr] items-center gap-4">
                <label className="text-sm text-slate-600 text-right">Complete Address</label>
                <input type="text" placeholder="Address Line 1" value={formData.postal_address_line1 || ''} onChange={(e) => setFormData({ ...formData, postal_address_line1: e.target.value })} className="px-3 py-2 border rounded text-sm" data-testid="edit-postal-line1" />
              </div>
              <div className="grid grid-cols-[120px_1fr] items-center gap-4">
                <label className="text-sm text-slate-600 text-right"></label>
                <input type="text" placeholder="Address Line 2" value={formData.postal_address_line2 || ''} onChange={(e) => setFormData({ ...formData, postal_address_line2: e.target.value })} className="px-3 py-2 border rounded text-sm" />
              </div>
              <div className="grid grid-cols-[120px_1fr] items-center gap-4">
                <label className="text-sm text-slate-600 text-right"></label>
                <input type="text" placeholder="Address Line 3" value={formData.postal_address_line3 || ''} onChange={(e) => setFormData({ ...formData, postal_address_line3: e.target.value })} className="px-3 py-2 border rounded text-sm" />
              </div>
              <div className="grid grid-cols-[120px_1fr] items-center gap-4">
                <label className="text-sm text-slate-600 text-right"></label>
                <input type="text" placeholder="Address Line 4" value={formData.postal_address_line4 || ''} onChange={(e) => setFormData({ ...formData, postal_address_line4: e.target.value })} className="px-3 py-2 border rounded text-sm" />
              </div>
              <div className="grid grid-cols-[120px_1fr] items-center gap-4">
                <label className="text-sm text-slate-600 text-right">City</label>
                <input type="text" value={formData.postal_city || ''} onChange={(e) => setFormData({ ...formData, postal_city: e.target.value })} className="px-3 py-2 border rounded text-sm" />
              </div>
              <div className="grid grid-cols-[120px_1fr] items-center gap-4">
                <label className="text-sm text-slate-600 text-right">Region</label>
                <input type="text" value={formData.postal_region || ''} onChange={(e) => setFormData({ ...formData, postal_region: e.target.value })} className="px-3 py-2 border rounded text-sm" />
              </div>
              <div className="grid grid-cols-[120px_1fr] items-center gap-4">
                <label className="text-sm text-slate-600 text-right">Postal Code</label>
                <input type="text" value={formData.postal_postal_code || ''} onChange={(e) => setFormData({ ...formData, postal_postal_code: e.target.value })} className="w-1/2 px-3 py-2 border rounded text-sm" />
              </div>
              <div className="grid grid-cols-[120px_1fr] items-center gap-4">
                <label className="text-sm text-slate-600 text-right">Country</label>
                <input type="text" value={formData.postal_country || ''} onChange={(e) => setFormData({ ...formData, postal_country: e.target.value })} className="px-3 py-2 border rounded text-sm" />
              </div>
            </div>
          </div>

          {/* Street Address */}
          <div>
            <div className="flex items-center justify-between mb-4 border-b pb-2">
              <h3 className="text-sm font-semibold text-[#f39c12] flex items-center gap-2"><MapPin size={16} />Street Address</h3>
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                <input type="checkbox" checked={sameAsPostal} onChange={(e) => setSameAsPostal(e.target.checked)} className="accent-[#3498db]" data-testid="same-as-postal" />
                Same as Postal address
              </label>
            </div>
            {!sameAsPostal && (
              <div className="space-y-4">
                <div className="grid grid-cols-[120px_1fr] items-center gap-4">
                  <label className="text-sm text-slate-600 text-right">Complete Address</label>
                  <input type="text" placeholder="Address Line 1" value={formData.street_address_line1 || ''} onChange={(e) => setFormData({ ...formData, street_address_line1: e.target.value })} className="px-3 py-2 border rounded text-sm" />
                </div>
                <div className="grid grid-cols-[120px_1fr] items-center gap-4">
                  <label className="text-sm text-slate-600 text-right"></label>
                  <input type="text" placeholder="Address Line 2" value={formData.street_address_line2 || ''} onChange={(e) => setFormData({ ...formData, street_address_line2: e.target.value })} className="px-3 py-2 border rounded text-sm" />
                </div>
                <div className="grid grid-cols-[120px_1fr] items-center gap-4">
                  <label className="text-sm text-slate-600 text-right">City</label>
                  <input type="text" value={formData.street_city || ''} onChange={(e) => setFormData({ ...formData, street_city: e.target.value })} className="px-3 py-2 border rounded text-sm" />
                </div>
                <div className="grid grid-cols-[120px_1fr] items-center gap-4">
                  <label className="text-sm text-slate-600 text-right">Region</label>
                  <input type="text" value={formData.street_region || ''} onChange={(e) => setFormData({ ...formData, street_region: e.target.value })} className="px-3 py-2 border rounded text-sm" />
                </div>
                <div className="grid grid-cols-[120px_1fr] items-center gap-4">
                  <label className="text-sm text-slate-600 text-right">Postal Code</label>
                  <input type="text" value={formData.street_postal_code || ''} onChange={(e) => setFormData({ ...formData, street_postal_code: e.target.value })} className="w-1/2 px-3 py-2 border rounded text-sm" />
                </div>
                <div className="grid grid-cols-[120px_1fr] items-center gap-4">
                  <label className="text-sm text-slate-600 text-right">Country</label>
                  <input type="text" value={formData.street_country || ''} onChange={(e) => setFormData({ ...formData, street_country: e.target.value })} className="px-3 py-2 border rounded text-sm" />
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button type="button" onClick={onClose} className="px-5 py-2 border rounded text-slate-600 hover:bg-slate-50">Cancel</button>
            <button type="submit" disabled={loading} className="px-5 py-2 bg-[#3498db] text-white rounded hover:bg-[#2980b9] flex items-center gap-2" data-testid="update-client-btn">
              {loading && <Loader2 size={16} className="animate-spin" />}
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const showPrices = canSeePrices(currentUser);
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [showEditModal, setShowEditModal] = useState(searchParams.get('edit') === 'true');
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [editAddress, setEditAddress] = useState(null);
  const [comment, setComment] = useState('');
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentText, setEditCommentText] = useState('');
  const [showCreateQuote, setShowCreateQuote] = useState(false);
  const [editingPM, setEditingPM] = useState(null);
  const [editPMData, setEditPMData] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [propSearch, setPropSearch] = useState('');
  const [propPageSize, setPropPageSize] = useState(8);
  const [propPage, setPropPage] = useState(1);

  const { data: client, isLoading, refetch: refetchClient } = useQuery({
    queryKey: ['client', id],
    queryFn: () => getClient(id).then(res => res.data),
  });

  const { data: addresses } = useQuery({
    queryKey: ['propertyAddresses', id],
    queryFn: () => getPropertyAddresses({ client_id: id }).then(res => res.data),
  });

  const { data: quotes } = useQuery({
    queryKey: ['clientQuotes', id],
    queryFn: () => getQuotes({ client_id: id }).then(res => res.data),
  });

  const { data: jobs } = useQuery({
    queryKey: ['clientJobs', id],
    queryFn: () => getJobs({ client_id: id }).then(res => res.data),
  });

  const { data: invoices } = useQuery({
    queryKey: ['clientInvoices', id],
    queryFn: () => getInvoices({ client_id: id }).then(res => res.data),
  });

  const { data: staff } = useQuery({
    queryKey: ['staff'],
    queryFn: () => getStaff().then(res => res.data),
  });

  const { data: clientPMs } = useQuery({
    queryKey: ['propertyManagers', id],
    queryFn: () => getPropertyManagers({ client_id: id }).then(r => r.data),
  });

  const updateClientMutation = useMutation({
    mutationFn: (data) => updateClient(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client', id] });
      setShowEditModal(false);
      searchParams.delete('edit');
      setSearchParams(searchParams);
      toast.success('Client updated');
    },
    onError: (error) => {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map(d => `${d.loc?.slice(-1)[0] || 'field'}: ${d.msg}`).join('; ')
        : detail || 'Failed to update client';
      toast.error(msg);
    },
  });

  const createAddressMutation = useMutation({
    mutationFn: createPropertyAddress,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['propertyAddresses', id] });
      queryClient.invalidateQueries({ queryKey: ['client', id] });
      setShowAddressModal(false);
      setEditAddress(null);
      toast.success('Property added');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to add property');
    },
  });

  const updateAddressMutation = useMutation({
    mutationFn: ({ addressId, data }) => updatePropertyAddress(addressId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['propertyAddresses', id] });
      queryClient.invalidateQueries({ queryKey: ['client', id] });
      setShowAddressModal(false);
      setEditAddress(null);
      toast.success('Property updated');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to update property');
    },
  });

  const deleteAddressMutation = useMutation({
    mutationFn: deletePropertyAddress,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['propertyAddresses', id] });
      toast.success('Property removed');
    },
  });

  const handleAddressSubmit = (data) => {
    if (editAddress) updateAddressMutation.mutate({ addressId: editAddress.id, data });
    else createAddressMutation.mutate(data);
  };

  const createQuoteMutation = useMutation({
    mutationFn: createQuote,
    onSuccess: (res) => {
      setShowCreateQuote(false);
      toast.success('Quote created');
      navigate(`/quotes/${res.data.quote_id}`);
    },
    onError: () => toast.error('Failed to create quote')
  });

  const updatePMMutation = useMutation({
    mutationFn: ({ pmId, data }) => updatePropertyManager(pmId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['propertyManagers', id] });
      setEditingPM(null);
      toast.success('Property Manager updated');
    },
    onError: () => toast.error('Failed to update PM')
  });

  // Stats
  const propertyCount = addresses?.length || 0;
  const propertyManagerCount = addresses?.filter(a => a.property_manager_id).length || 0;
  const jobCount = jobs?.length || 0;
  const ongoingJobCount = jobs?.filter(j => j.status < 3).length || 0;
  const invoiceCount = invoices?.length || 0;
  const quoteCount = quotes?.length || 0;

  // Get property manager info
  const getPropertyManager = (pmId) => {
    if (!pmId) return null;
    const pm = clientPMs?.find(p => p.id === pmId);
    if (pm) return { name: `${pm.first_name} ${pm.last_name}`.trim(), phone: pm.phone, email: pm.email };
    // Fallback: check staff (for legacy data)
    const staffMember = staff?.find(s => s.id === pmId);
    return staffMember ? { name: `${staffMember.first_name} ${staffMember.last_name}`.trim(), phone: staffMember.phone, email: staffMember.email } : null;
  };

  // Filter + paginate property addresses for the search box in the Properties table.
  // Searches across address lines, city, region, postcode, tenant, owner, and the linked PM.
  const filteredAddresses = React.useMemo(() => {
    if (!addresses) return [];
    const q = propSearch.trim().toLowerCase();
    if (!q) return addresses;
    return addresses.filter(addr => {
      const pm = getPropertyManager(addr.property_manager_id);
      const haystack = [
        addr.address_line1, addr.address_line2, addr.address_line3, addr.address_line4,
        addr.city, addr.region, addr.postal_code, addr.country,
        addr.tenant_first_name, addr.tenant_last_name, addr.tenant_email, addr.tenant_phone,
        addr.owner_first_name, addr.owner_last_name, addr.owner_email, addr.owner_phone,
        pm?.name, pm?.email, pm?.phone,
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addresses, propSearch, clientPMs, staff]);

  const propTotalPages = Math.max(1, Math.ceil(filteredAddresses.length / propPageSize));
  const propSafePage = Math.min(propPage, propTotalPages);
  const pagedAddresses = filteredAddresses.slice((propSafePage - 1) * propPageSize, propSafePage * propPageSize);

  // Reset to page 1 whenever the search term or page size changes
  useEffect(() => { setPropPage(1); }, [propSearch, propPageSize]);

  // Get full address string
  const getFullAddress = () => {
    // Try client's postal/street address first
    const parts = [
      client?.sa_address_line1 || client?.po_address_line1 || client?.postal_address_line1 || client?.street_address_line1,
      client?.sa_address_line2 || client?.po_address_line2 || client?.postal_address_line2,
      client?.sa_city || client?.po_city || client?.postal_city || client?.street_city,
      client?.sa_region || client?.po_region || client?.postal_region || client?.street_region,
      client?.sa_postal_code || client?.po_postal_code || client?.postal_postal_code,
      client?.sa_country || client?.po_country || client?.postal_country || client?.street_country
    ].filter(Boolean);
    if (parts.length > 1 || (parts.length === 1 && parts[0] !== 'Australia')) return parts.join(' ');
    // Fallback to first property address
    if (addresses?.length > 0) {
      const addr = addresses[0];
      const addrParts = [addr.address_line1, addr.address_line2, addr.city, addr.region, addr.postal_code, addr.country].filter(Boolean);
      if (addrParts.length > 0) return addrParts.join(' ');
    }
    return parts.join(' ');
  };

  if (isLoading) {
    return <div className="flex items-center justify-center py-12"><Loader2 size={32} className="animate-spin text-primary" /></div>;
  }

  return (
    <div className="animate-fadeIn" data-testid="client-detail">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm mb-4">
        <Link to="/clients" className="text-[#3498db] hover:underline">Client</Link>
        <ChevronRight size={16} className="text-slate-400" />
        <span className="text-slate-600">Details</span>
      </div>

      {/* Header Section */}
      <div className="bg-white rounded-lg border shadow-sm p-6 mb-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">{client?.contact_name}</h1>
            {getFullAddress() && (
              <div className="flex items-center gap-2 text-sm text-[#3498db] mt-1">
                <MapPin size={14} />
                <a href={mapsLink(getFullAddress())} target="_blank" rel="noopener noreferrer" className="hover:underline inline-flex items-center gap-1" data-testid="client-header-address-link">{getFullAddress()}<ExternalLink size={12} className="opacity-60" /></a>
              </div>
            )}
            {client?.contact_email && (
              <div className="flex items-center gap-2 text-sm text-slate-600 mt-1">
                <Mail size={14} />
                <span>{client.contact_email}</span>
              </div>
            )}
            {client?.contact_phone && (
              <div className="flex items-center gap-2 text-sm text-slate-600 mt-1">
                <Phone size={14} />
                <span>{client.contact_phone}</span>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <button className="px-4 py-2 bg-slate-100 text-slate-700 rounded hover:bg-slate-200">Email Quotes</button>
            <button onClick={() => setShowCreateQuote(true)} className="px-4 py-2 bg-[#3498db] text-white rounded hover:bg-[#2980b9]" data-testid="create-quote-btn">Create Quote</button>
            <Link to={`/jobs?client_id=${id}`} className="px-4 py-2 bg-[#18bc9c] text-white rounded hover:bg-[#15a589]" data-testid="create-job-btn">Create Job</Link>
            <button onClick={() => setShowEditModal(true)} className="px-4 py-2 bg-[#f39c12] text-white rounded hover:bg-[#d68910]" data-testid="edit-client-btn">Edit Client</button>
            <button onClick={() => { setEditAddress(null); setShowAddressModal(true); }} className="px-4 py-2 bg-[#3498db] text-white rounded hover:bg-[#2980b9]" data-testid="add-property-address-btn">Add Property</button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-6 mb-6">
        {/* Blue Card - Properties */}
        <div className="bg-[#3498db] rounded-lg overflow-hidden">
          <div className="h-24 flex items-center justify-center">
            <Home size={40} className="text-white/80" />
          </div>
          <div className="bg-white px-4 py-3 flex">
            <div className="flex-1 text-center border-r">
              <div className="text-2xl font-bold text-slate-800">{propertyCount}</div>
              <div className="text-xs text-slate-500 uppercase">Properties</div>
            </div>
            <div className="flex-1 text-center">
              <div className="text-2xl font-bold text-slate-800">{propertyManagerCount}</div>
              <div className="text-xs text-slate-500 uppercase">Property Manager</div>
            </div>
          </div>
        </div>

        {/* Red Card - Jobs */}
        <div className="bg-[#e74c3c] rounded-lg overflow-hidden">
          <div className="h-24 flex items-center justify-center">
            <Briefcase size={40} className="text-white/80" />
          </div>
          <div className="bg-white px-4 py-3 flex">
            <div className="flex-1 text-center border-r">
              <div className="text-2xl font-bold text-slate-800">{jobCount}</div>
              <div className="text-xs text-slate-500 uppercase">Job(s)</div>
            </div>
            <div className="flex-1 text-center">
              <div className="text-2xl font-bold text-slate-800">{ongoingJobCount}</div>
              <div className="text-xs text-slate-500 uppercase">Ongoing</div>
            </div>
          </div>
        </div>

        {/* Green Card - Invoices & Quotes */}
        <div className="bg-[#18bc9c] rounded-lg overflow-hidden">
          <div className="h-24 flex items-center justify-center">
            <FileText size={40} className="text-white/80" />
          </div>
          <div className="bg-white px-4 py-3 flex">
            <div className="flex-1 text-center border-r">
              <div className="text-2xl font-bold text-slate-800">{invoiceCount}</div>
              <div className="text-xs text-slate-500 uppercase">Invoice</div>
            </div>
            <div className="flex-1 text-center">
              <div className="text-2xl font-bold text-slate-800">{quoteCount}</div>
              <div className="text-xs text-slate-500 uppercase">Quotes</div>
            </div>
          </div>
        </div>
      </div>

      {/* Properties Section */}
      <div className="bg-white rounded-lg border shadow-sm mb-6">
        <div className="p-4 bg-slate-50 border-b">
          <h2 className="font-semibold text-slate-700">Properties</h2>
        </div>
        <div className="p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2 text-sm">
              <span>Show</span>
              <select
                value={propPageSize}
                onChange={(e) => setPropPageSize(Number(e.target.value))}
                className="border rounded px-2 py-1"
                data-testid="properties-page-size"
              >
                <option value={8}>8</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>entries</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span>Search:</span>
              <input
                type="text"
                value={propSearch}
                onChange={(e) => setPropSearch(e.target.value)}
                placeholder="Search address, PM, tenant..."
                className="border rounded px-3 py-1"
                data-testid="properties-search"
              />
            </div>
          </div>
          <table className="w-full">
            <thead className="border-b text-sm text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Address ↕</th>
                <th className="px-3 py-2 text-left font-medium">Property Manager ↕</th>
                <th className="px-3 py-2 text-left font-medium">Tenant ↕</th>
                <th className="px-3 py-2 text-left font-medium">Owner ↕</th>
                <th className="px-3 py-2 text-center font-medium">Quotes ↕</th>
                <th className="px-3 py-2 text-center font-medium">Jobs ↕</th>
                <th className="px-3 py-2 text-right font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y text-sm">
              {filteredAddresses.length === 0 ? (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-500" data-testid="properties-empty">
                  {propSearch ? `No properties match "${propSearch}"` : 'No properties found'}
                </td></tr>
              ) : (
                pagedAddresses.map((addr) => {
                  const pm = getPropertyManager(addr.property_manager_id);
                  const addrQuotes = quotes?.filter(q => q.property_address_id === addr.id).length || 0;
                  const addrJobs = jobs?.filter(j => j.property_address_id === addr.id).length || 0;
                  return (
                    <tr key={addr.id} className="hover:bg-slate-50">
                      <td className="px-3 py-3">
                        <AddressLink address={`${addr.address_line1} ${addr.city || ''}`.trim()} />
                      </td>
                      <td className="px-3 py-3">
                        {pm ? (
                          <div>
                            <span className="font-medium">{pm.name}</span>
                            {pm.email && <span className="text-slate-500 text-xs block">{pm.email}</span>}
                            {pm.phone && <span className="text-slate-500 text-xs block">{pm.phone}</span>}
                          </div>
                        ) : '-'}
                      </td>
                      <td className="px-3 py-3">
                        {addr.tenant_first_name ? `${addr.tenant_first_name} ${addr.tenant_last_name}` : '-'}
                      </td>
                      <td className="px-3 py-3">
                        {addr.owner_first_name ? `${addr.owner_first_name} ${addr.owner_last_name}` : '-'}
                      </td>
                      <td className="px-3 py-3 text-center text-[#3498db]">{addrQuotes}</td>
                      <td className="px-3 py-3 text-center text-[#3498db]">{addrJobs}</td>
                      <td className="px-3 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          <button onClick={() => { if(window.confirm('Remove this property?')) deleteAddressMutation.mutate(addr.id); }} className="px-2 py-1 bg-[#e74c3c] text-white text-xs rounded hover:bg-[#c0392b]">Remove</button>
                          <button onClick={() => { setEditAddress(addr); setShowAddressModal(true); }} className="px-2 py-1 bg-[#f39c12] text-white text-xs rounded hover:bg-[#d68910]">Edit</button>
                          <Link to={`/properties/${addr.id}`} className="px-2 py-1 bg-[#3498db] text-white text-xs rounded hover:bg-[#2980b9]">Details</Link>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          {filteredAddresses.length > 0 && (
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mt-4 text-sm text-slate-600 gap-2">
              <span data-testid="properties-count">
                Showing {(propSafePage - 1) * propPageSize + 1} to {Math.min(propSafePage * propPageSize, filteredAddresses.length)} of {filteredAddresses.length} entries
                {propSearch && addresses && filteredAddresses.length !== addresses.length && ` (filtered from ${addresses.length})`}
              </span>
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => setPropPage(p => Math.max(1, p - 1))}
                  disabled={propSafePage <= 1}
                  className="px-3 py-1 border rounded disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50"
                  data-testid="properties-prev"
                >Previous</button>
                {Array.from({ length: propTotalPages }, (_, i) => i + 1).map(n => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setPropPage(n)}
                    className={`px-3 py-1 rounded ${n === propSafePage ? 'bg-[#3498db] text-white' : 'border hover:bg-slate-50'}`}
                    data-testid={`properties-page-${n}`}
                  >{n}</button>
                ))}
                <button
                  type="button"
                  onClick={() => setPropPage(p => Math.min(propTotalPages, p + 1))}
                  disabled={propSafePage >= propTotalPages}
                  className="px-3 py-1 border rounded disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50"
                  data-testid="properties-next"
                >Next</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Property Managers Section */}
      {clientPMs?.length > 0 && (
        <div className="bg-white rounded-lg border shadow-sm mb-6">
          <div className="p-4 bg-slate-50 border-b flex items-center justify-between">
            <h2 className="font-semibold text-slate-700">Property Managers ({clientPMs.length})</h2>
          </div>
          <div className="p-4 overflow-x-auto">
            <table className="w-full text-sm" data-testid="pm-table">
              <thead className="border-b text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Name</th>
                  <th className="px-3 py-2 text-left font-medium">Email</th>
                  <th className="px-3 py-2 text-left font-medium">Phone</th>
                  <th className="px-3 py-2 text-right font-medium"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {clientPMs.map(pm => (
                  <tr key={pm.id} className="hover:bg-slate-50">
                    {editingPM === pm.id ? (
                      <>
                        <td className="px-3 py-2">
                          <div className="flex gap-1">
                            <input type="text" value={editPMData.first_name} onChange={e => setEditPMData({...editPMData, first_name: e.target.value})} className="px-2 py-1 border rounded text-sm w-24" placeholder="First" data-testid="edit-pm-first" />
                            <input type="text" value={editPMData.last_name} onChange={e => setEditPMData({...editPMData, last_name: e.target.value})} className="px-2 py-1 border rounded text-sm w-24" placeholder="Last" data-testid="edit-pm-last" />
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <input type="email" value={editPMData.email} onChange={e => setEditPMData({...editPMData, email: e.target.value})} className="px-2 py-1 border rounded text-sm w-full" placeholder="Email" data-testid="edit-pm-email" />
                        </td>
                        <td className="px-3 py-2">
                          <input type="text" value={editPMData.phone} onChange={e => setEditPMData({...editPMData, phone: e.target.value})} className="px-2 py-1 border rounded text-sm w-full" placeholder="Phone" data-testid="edit-pm-phone" />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex justify-end gap-1">
                            <button onClick={() => setEditingPM(null)} className="px-2 py-1 text-xs border rounded text-slate-600">Cancel</button>
                            <button onClick={() => updatePMMutation.mutate({ pmId: pm.id, data: editPMData })} disabled={updatePMMutation.isPending} className="px-2 py-1 text-xs bg-[#18bc9c] text-white rounded flex items-center gap-1" data-testid="save-pm-edit-btn">
                              {updatePMMutation.isPending && <Loader2 size={10} className="animate-spin" />} <Save size={12} /> Save
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-3 py-3 font-medium">{pm.first_name} {pm.last_name}</td>
                        <td className="px-3 py-3 text-slate-600">{pm.email || <span className="text-slate-400 italic">No email</span>}</td>
                        <td className="px-3 py-3 text-slate-600">{pm.phone || <span className="text-slate-400 italic">No phone</span>}</td>
                        <td className="px-3 py-3 text-right">
                          <button onClick={() => { setEditingPM(pm.id); setEditPMData({ first_name: pm.first_name || '', last_name: pm.last_name || '', email: pm.email || '', phone: pm.phone || '' }); }} className="px-2 py-1 text-xs bg-[#f39c12] text-white rounded hover:bg-[#d68910]" data-testid={`edit-pm-${pm.id}`}>
                            <Edit size={12} className="inline" /> Edit
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Quotes Section */}
      <div className="bg-white rounded-lg border shadow-sm mb-6">
        <div className="p-4 bg-slate-50 border-b">
          <h2 className="font-semibold text-slate-700">Quotes</h2>
        </div>
        <div className="p-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Date</th>
                <th className="px-3 py-2 text-left font-medium">Quote No.</th>
                <th className="px-3 py-2 text-left font-medium">Address</th>
                <th className="px-3 py-2 text-left font-medium">Job Titles</th>
                {showPrices && <th className="px-3 py-2 text-right font-medium">Total Cost</th>}
                <th className="px-3 py-2 text-center font-medium">Jobs</th>
                <th className="px-3 py-2 text-center font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {quotes?.length === 0 ? (
                <tr><td colSpan={showPrices ? 7 : 6} className="px-3 py-8 text-center text-slate-500">No quotes found</td></tr>
              ) : (
                quotes?.map((quote) => (
                  <tr key={quote.id} className="hover:bg-slate-50">
                    <td className="px-3 py-3">{formatDate(quote.created_at)}</td>
                    <td className="px-3 py-3"><Link to={`/quotes/${quote.id}`} className="text-[#3498db] hover:underline">{quote.quote_number}</Link></td>
                    <td className="px-3 py-3"><AddressLink address={quote.property_address} /></td>
                    <td className="px-3 py-3">{quote.descriptions?.map(d => d.job_title).join(', ') || '-'}</td>
                    {showPrices && <td className="px-3 py-3 text-right">${(quote.total_cost || 0).toFixed(2)}</td>}
                    <td className="px-3 py-3 text-center">{quote.job_id ? '1' : '0'}</td>
                    <td className="px-3 py-3 text-center">
                      <span className={`px-2 py-1 rounded text-xs ${quote.status === 2 ? 'bg-green-100 text-green-700' : quote.status === 1 ? 'bg-yellow-100 text-yellow-700' : 'bg-slate-100 text-slate-700'}`}>
                        {['Draft', 'For Submission', 'Accepted', 'Job Booked', 'Invoiced', 'Cancelled'][quote.status] || 'Draft'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Job Sheets Section */}
      <div className="bg-white rounded-lg border shadow-sm mb-6">
        <div className="p-4 bg-slate-50 border-b">
          <h2 className="font-semibold text-slate-700">Job Sheets</h2>
        </div>
        <div className="p-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Date</th>
                <th className="px-3 py-2 text-left font-medium">Job No.</th>
                <th className="px-3 py-2 text-left font-medium">Quote No.</th>
                <th className="px-3 py-2 text-left font-medium">Address</th>
                <th className="px-3 py-2 text-left font-medium">Jobs</th>
                <th className="px-3 py-2 text-center font-medium">Completed</th>
                <th className="px-3 py-2 text-center font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {jobs?.length === 0 ? (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-500">No jobs found</td></tr>
              ) : (
                jobs?.map((job) => (
                  <tr key={job.id} className="hover:bg-slate-50">
                    <td className="px-3 py-3">{formatDate(job.created_at)}</td>
                    <td className="px-3 py-3"><Link to={`/jobs/${job.id}`} className="text-[#3498db] hover:underline">{job.job_number}</Link></td>
                    <td className="px-3 py-3">{job.quote_number || '-'}</td>
                    <td className="px-3 py-3"><AddressLink address={job.property_address} /></td>
                    <td className="px-3 py-3">{job.descriptions?.map(d => d.job_title).join(', ') || '-'}</td>
                    <td className="px-3 py-3 text-center">{job.completed_date ? formatDate(job.completed_date) : '-'}</td>
                    <td className="px-3 py-3 text-center">
                      <span className={`px-2 py-1 rounded text-xs ${job.status === 3 ? 'bg-green-100 text-green-700' : job.status === 2 ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {['Scheduled', 'In Progress', 'On Hold', 'Completed', 'Cancelled'][job.status] || 'Scheduled'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Comments Section */}
      <div className="bg-white rounded-lg border shadow-sm">
        <div className="p-4 bg-slate-50 border-b">
          <h2 className="font-semibold text-slate-700 flex items-center gap-2">
            <MessageCircle size={18} />
            Comments
          </h2>
        </div>
        <div className="p-4">
          {(!client?.comments || client.comments.length === 0) ? (
            <div className="text-slate-500 text-sm flex items-center gap-2 py-4 justify-center">
              <AlertCircle size={16} />
              <span>There is no comment yet.</span>
            </div>
          ) : (
            <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
              {client.comments.map((c, idx) => {
                const canManage = currentUser?.id === c.added_by_user_id || [0, 5].includes(currentUser?.user_type);
                const isEditing = editingCommentId === c.id;
                return (
                <div key={c.id || `comment-${idx}`} className="bg-slate-50 p-3 rounded" data-testid={`client-comment-${idx}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <User size={14} className="text-slate-400" />
                    <span className="text-sm font-medium text-slate-700">{c.user_name || 'Unknown User'}</span>
                    <span className="text-xs text-slate-400">{formatDateTime(c.created_at)}</span>
                    {canManage && !isEditing && (
                      <div className="ml-auto flex items-center gap-2">
                        <button onClick={() => { setEditingCommentId(c.id); setEditCommentText(c.comment); }} className="text-slate-400 hover:text-primary transition-colors" title="Edit comment" data-testid={`client-comment-edit-${idx}`}><Edit size={14} /></button>
                        <button onClick={async () => {
                          if (!window.confirm('Delete this comment?')) return;
                          try { await deleteComment(c.id); refetchClient(); toast.success('Comment deleted'); }
                          catch { toast.error('Failed to delete comment'); }
                        }} className="text-slate-400 hover:text-red-600 transition-colors" title="Delete comment" data-testid={`client-comment-delete-${idx}`}><Trash2 size={14} /></button>
                      </div>
                    )}
                  </div>
                  {isEditing ? (
                    <div className="flex gap-2 items-start mt-1">
                      <input type="text" value={editCommentText} onChange={(e) => setEditCommentText(e.target.value)} className="flex-1 px-3 py-1.5 border border-slate-300 rounded text-sm" autoFocus data-testid={`client-comment-edit-input-${idx}`} />
                      <button onClick={async () => {
                        if (!editCommentText.trim()) return;
                        try { await updateComment(c.id, editCommentText.trim()); setEditingCommentId(null); refetchClient(); toast.success('Comment updated'); }
                        catch { toast.error('Failed to update comment'); }
                      }} className="px-3 py-1.5 bg-primary text-white rounded text-sm hover:bg-primary-hover" data-testid={`client-comment-save-${idx}`}><Save size={14} /></button>
                      <button onClick={() => setEditingCommentId(null)} className="px-3 py-1.5 bg-slate-100 border border-slate-300 rounded text-sm hover:bg-slate-200" data-testid={`client-comment-cancel-${idx}`}><X size={14} /></button>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-600 whitespace-pre-wrap">{c.comment}</p>
                  )}
                </div>
                );
              })}
            </div>
          )}
          <form onSubmit={async (e) => {
            e.preventDefault();
            if (!comment.trim()) return;
            try {
              await createComment({ entity_type: 'client', entity_id: id, comment });
              setComment('');
              refetchClient();
              toast.success('Comment added');
            } catch { toast.error('Failed to add comment'); }
          }} className="flex gap-2" data-testid="client-comment-form">
            <input
              type="text"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add a comment..."
              className="flex-1 px-3 py-2 border border-slate-300 rounded text-sm"
              data-testid="client-comment-input"
            />
            <button type="submit" className="px-4 py-2 bg-primary text-white rounded text-sm hover:bg-primary-hover flex items-center gap-1.5" data-testid="client-comment-submit">
              <Send size={14} /> Add
            </button>
          </form>
        </div>
      </div>

      {/* Modals */}
      <EditClientModal isOpen={showEditModal} onClose={() => { setShowEditModal(false); searchParams.delete('edit'); setSearchParams(searchParams); }} onSubmit={(data) => updateClientMutation.mutate(data)} loading={updateClientMutation.isPending} client={client} />
      <PropertyAddressModal isOpen={showAddressModal} onClose={() => { setShowAddressModal(false); setEditAddress(null); }} onSubmit={handleAddressSubmit} loading={createAddressMutation.isPending || updateAddressMutation.isPending} editAddress={editAddress} clientId={id} />
      <CreateQuoteModal isOpen={showCreateQuote} onClose={() => setShowCreateQuote(false)} onSubmit={(data) => createQuoteMutation.mutate(data)} loading={createQuoteMutation.isPending} clientId={id} addresses={addresses} />
    </div>
  );
}

// Create Quote Modal
function CreateQuoteModal({ isOpen, onClose, onSubmit, loading, clientId, addresses }) {
  const defaultExpiry = getPerthDateOffset(30);
  const [formData, setFormData] = useState({
    client_id: clientId,
    property_address_id: '',
    job_title: '',
    description: '',
    expiry_date: defaultExpiry,
  });

  useEffect(() => {
    if (isOpen) {
      setFormData({
        client_id: clientId,
        property_address_id: addresses?.length === 1 ? addresses[0].id : '',
        job_title: '',
        description: '',
        expiry_date: defaultExpiry,
      });
    }
  }, [isOpen, clientId, addresses, defaultExpiry]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.property_address_id) { toast.error('Please select a property'); return; }
    if (!formData.job_title) { toast.error('Please enter a job title'); return; }
    onSubmit(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b flex items-center justify-between bg-slate-50">
          <h2 className="text-lg font-semibold text-slate-700 flex items-center gap-2"><FileText size={20} />Create Quote</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="close-create-quote"><X size={24} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Property *</label>
            <SearchableSelect
              value={formData.property_address_id}
              onChange={(v) => setFormData({ ...formData, property_address_id: v })}
              options={(addresses || []).map((addr) => ({
                value: addr.id,
                label: [addr.address_line1, addr.city, addr.region, addr.postal_code].filter(Boolean).join(', '),
              }))}
              placeholder="Select Property Address"
              testId="quote-property-select"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Job Title *</label>
            <input
              type="text"
              value={formData.job_title}
              onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
              placeholder="Enter job title"
              className="w-full px-3 py-2 border rounded text-sm"
              data-testid="quote-job-title"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Expiry Date</label>
            <DatePicker
              value={formData.expiry_date}
              onChange={(v) => setFormData({ ...formData, expiry_date: v })}
              testId="quote-expiry-date"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Description</label>
            <RichTextEditor
              value={formData.description}
              onChange={(val) => setFormData({ ...formData, description: val })}
              placeholder="Enter description..."
            />
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button type="button" onClick={onClose} className="px-5 py-2 border rounded text-slate-600 hover:bg-slate-50">Cancel</button>
            <button type="submit" disabled={loading} className="px-5 py-2 bg-[#3498db] text-white rounded hover:bg-[#2980b9] flex items-center gap-2" data-testid="submit-create-quote">
              {loading && <Loader2 size={16} className="animate-spin" />}
              Create Quote
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ClientDetail;
